import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { ProjectTemplateModal } from "@components/modals/ProjectTemplateModal";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState, useCallback } from "react";
import { projectTemplatesApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { defaultProjectTemplates } from "./defaultProjectTemplates";
import { Template } from "types";

export const ProjectTemplates = () => {
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const isLoadingProjectTemplates = useProjectStore((state) => state.isLoadingProjectTemplates);
  const userData = useDataStore((state) => state.userData);
  const [value, setValue] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);

  const handleCreateDefaults = async () => {
    if (!userData?.org_id) return;

    setIsCreatingDefaults(true);
    try {
      let created = 0;
      for (const template of defaultProjectTemplates) {
        const result = await projectTemplatesApi.create({
          name: template.name,
          org_id: userData.org_id,
          status: "active",
          themes: template.themes,
          folders: template.folders,
        });
        useProjectStore.getState().setProjectTemplates([
          ...useProjectStore.getState().projectTemplates,
          result,
        ]);
        created++;
      }
      showNotification({
        type: "success",
        message: `${created} modèle(s) par défaut ajouté(s)`,
      });
    } catch (error) {
      console.error("Error creating default project templates:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la création des modèles",
      });
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  const sortedTemplates = [...projectTemplates].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const handleReorder = useCallback(async (templateId: string, direction: "up" | "down") => {
    const sorted = [...projectTemplates].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const idx = sorted.findIndex((t) => t.id === templateId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const updates: { id: string; order: number }[] = sorted.map((t, i) => ({ id: t.id, order: i }));
    // Swap
    const tmp = updates[idx].order;
    updates[idx].order = updates[swapIdx].order;
    updates[swapIdx].order = tmp;

    // Optimistic update
    const updated = projectTemplates.map((t) => {
      const u = updates.find((u) => u.id === t.id);
      return u ? { ...t, order: u.order } : t;
    });
    useProjectStore.getState().setProjectTemplates(updated);

    // Persist
    for (const u of updates.filter((_, i) => i === idx || i === swapIdx)) {
      projectTemplatesApi.update(u.id, { order: u.order }).catch(() => {});
    }
  }, [projectTemplates]);

  const getTemplateCounts = (template: any) => {
    const themes = template.themes?.length || 0;
    let steps = 0;
    let controlpoints = 0;
    (template.themes || []).forEach((theme: any) => {
      steps += theme.steps?.length || 0;
      (theme.steps || []).forEach((step: any) => {
        controlpoints += step.controlpoints?.length || 0;
      });
    });
    return { themes, steps, controlpoints };
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
      {(selectedTemplate || newTemplateOpen) && (
        <ProjectTemplateModal
          templateId={selectedTemplate || ""}
          onClose={() => {
            setSelectedTemplate(null);
            setNewTemplateOpen(false);
          }}
        />
      )}
      <div className="flex flex-col w-full max-w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Modèles de projets</Text1>
          <Text2>Configurez vos modèles de projets ici.</Text2>
          <Text3>
            Les modèles de projets définissent les thèmes, étapes et points de
            contrôle utilisés lors de la création d'un nouveau projet.
          </Text3>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-full max-w-400">
            <SearchField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="construction, rénovation..."
            />
          </div>
          {projectTemplates.length === 0 && (
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={handleCreateDefaults}
              disabled={isCreatingDefaults}
            >
              {isCreatingDefaults
                ? "Création en cours..."
                : "Ajouter les modèles par défaut"}
            </SecondaryButton>
          )}
        </div>

        <div className="flex flex-col divide-y mt-4">
          {sortedTemplates
            .filter((template) =>
              template.infos.general.name
                .toLowerCase()
                .includes(value.toLowerCase()),
            )
            .map((template, index) => {
              const counts = getTemplateCounts(template);
              return (
                <div
                  key={template.id}
                  className="flex flex-row rounded-xl items-center px-4 py-2"
                >
                  <div className="flex flex-col gap-0.5 mr-3">
                    <button
                      className="p-0.5 rounded hover:bg-neutral-100 text-neutral-300 hover:text-neutral-600 disabled:opacity-20"
                      onClick={() => handleReorder(template.id, "up")}
                      disabled={index === 0}
                    >
                      <Icon1 name="up2" size={14} />
                    </button>
                    <button
                      className="p-0.5 rounded hover:bg-neutral-100 text-neutral-300 hover:text-neutral-600 disabled:opacity-20"
                      onClick={() => handleReorder(template.id, "down")}
                      disabled={index === sortedTemplates.length - 1}
                    >
                      <Icon1 name="down2" size={14} />
                    </button>
                  </div>
                  <div className="flex flex-col w-250">
                    <div className="text-h2 font-medium">
                      {template.infos.general.name}
                    </div>
                  </div>
                  <div className="text-h3 text-neutral-400 mr-auto flex flex-row gap-3">
                    <span>{counts.themes} thèmes</span>
                    <span>{counts.steps} étapes</span>
                    <span>{counts.controlpoints} points</span>
                  </div>
                  <SecondaryButton
                    label="Modifier"
                    variant="small"
                    onClick={() => setSelectedTemplate(template.id)}
                  />
                </div>
              );
            })}
        </div>

        <Button
          className="mt-4"
          label="Nouveau modèle de projet"
          onClick={() => setNewTemplateOpen(true)}
        />
      </div>
    </div>
  );
};
